// Step 3: the admin checks how each source value lands in Moderniser. The run
// arrives with suggestions already filled into `mapping`; this edits a local
// copy and saves it back before the preview runs.
import { useEffect, useRef, useState } from "react"
import { api, type BoardChoice, type MappingDraft, type MigrationRun } from "../../lib/api"
import { CREATE_ON_BOARD, formatCount, missingChoices } from "../../lib/migrations"
import { Badge, Button, ErrorNote, Select } from "../ui"
import { Note, StepLayout } from "./parts"

const CATEGORY_DOT: Record<string, string> = {
  todo: "bg-faint",
  in_progress: "bg-interactive",
  done: "bg-success",
}

export function MappingStep({ run, onSaved }: { run: MigrationRun; onSaved: (run: MigrationRun) => void }) {
  const suggestions = run.suggestions!
  const [draft, setDraft] = useState<MappingDraft>(() => structuredClone(run.mapping!))
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const dirty = useRef(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const save = async (next: MappingDraft) => {
    clearTimeout(saveTimer.current)
    const { run: saved } = await api.patch<{ run: MigrationRun }>(`/migrations/${run.id}`, { mapping: next })
    dirty.current = false
    return saved
  }

  // Saved a moment after each change, so leaving the page keeps the edits.
  useEffect(() => {
    if (!dirty.current) return
    saveTimer.current = setTimeout(() => {
      save(draft).catch(setError)
    }, 800)
    return () => clearTimeout(saveTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  const change = (update: (d: MappingDraft) => void) => {
    setDraft((current) => {
      const next = structuredClone(current)
      update(next)
      return next
    })
    dirty.current = true
  }

  const missing = missingChoices(draft)

  const preview = async () => {
    setBusy(true)
    setError(null)
    try {
      // The PATCH goes first and is awaited: the preview must plan with the
      // mapping on screen, not the one saved a keystroke ago.
      await save(draft)
      const { run: next } = await api.post<{ run: MigrationRun }>(`/migrations/${run.id}/preview`)
      onSaved(next)
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  // Moves the run onto the project that already holds this source. The server
  // works out the suggestions again against that project's board.
  const retarget = async () => {
    const prior = suggestions.priorImport
    if (!prior) return
    setBusy(true)
    setError(null)
    try {
      clearTimeout(saveTimer.current)
      const { run: next } = await api.patch<{ run: MigrationRun }>(`/migrations/${run.id}`, {
        target: { projectId: prior.projectId },
      })
      dirty.current = false
      setDraft(structuredClone(next.mapping!))
      onSaved(next)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <StepLayout
      title="Review the mapping"
      intro={'We matched the project\'s values to Moderniser\'s. Check each one. Rows marked "Needs a choice" must be set before you can preview.'}
      guide={{
        points: [
          "Every state seen in the project's history is listed, so the status history is rebuilt correctly.",
          '"Create on the board" adds a status or type to the project instead of reusing one.',
          "The count shows how many issues use each value now or did before.",
        ],
        tip: "Unsure? Keep the suggestion. You can rename statuses in the project's board settings after the import.",
      }}
      footer={
        <>
          <span className="ml-auto text-xs text-muted">
            {missing.length > 0 && `${missing.length} value${missing.length > 1 ? "s need" : " needs"} a choice`}
          </span>
          <Button type="button" onClick={preview} disabled={busy || missing.length > 0}>
            {busy ? "Starting preview…" : "Preview the import"}
          </Button>
        </>
      }
    >
      <ErrorNote error={error} />

      {suggestions.priorImport && (
        <Note tone="warning">
          <span className="flex flex-wrap items-center gap-3">
            <span className="min-w-0 flex-1">
              {run.sourceProject.key} was already imported into {suggestions.priorImport.key} ({suggestions.priorImport.name}),
              which holds {formatCount(suggestions.priorImport.tasks)} of its tasks. A YouTrack project can only live in one
              Moderniser project, so import into {suggestions.priorImport.key} to bring in what changed since.
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={retarget} disabled={busy}>
              Change target to {suggestions.priorImport.key}
            </Button>
          </span>
        </Note>
      )}

      <MapSection title="States → statuses" note={`${suggestions.states.length} values, including ones only in history`}>
        {suggestions.states.map(({ value, uses }) => {
          const choice = draft.status[value] ?? null
          return (
            <MapRow
              key={value}
              value={value}
              uses={`${formatCount(uses)} uses`}
              attention={!choice}
              badge={choice ? <Badge tone="success">Suggested</Badge> : <Badge tone="warning">Needs a choice</Badge>}
            >
              <ChoiceSelect
                label={`Status for ${value}`}
                value={choice ?? ""}
                choices={suggestions.board.statuses}
                placeholder="Choose a status…"
                allowCreate
                onChange={(v) => change((d) => (d.status[value] = v || null))}
              />
            </MapRow>
          )
        })}
      </MapSection>

      <MapSection title="Types" note={`${suggestions.types.length} values`}>
        {suggestions.types.map(({ value, uses }) => (
          <MapRow key={value} value={value} uses={`${formatCount(uses)} uses`} badge={<Badge tone="success">Suggested</Badge>}>
            <ChoiceSelect
              label={`Type for ${value}`}
              value={draft.type[value] ?? CREATE_ON_BOARD}
              choices={suggestions.board.types}
              allowCreate
              onChange={(v) => change((d) => (d.type[value] = v))}
            />
          </MapRow>
        ))}
      </MapSection>

      {suggestions.priorityValues.length > 0 && (
        <MapSection title="Priorities" note={`${suggestions.priorityValues.length} values`}>
          {suggestions.priorityValues.map(({ value, uses }) => (
            <MapRow key={value} value={value} uses={`${formatCount(uses)} issues`} badge={<Badge tone="success">Suggested</Badge>}>
              <ChoiceSelect
                label={`Priority for ${value}`}
                value={draft.priority[value] ?? (suggestions.priorities.some((p) => p.id === "none") ? "none" : CREATE_ON_BOARD)}
                choices={suggestions.priorities}
                allowCreate
                onChange={(v) => change((d) => (d.priority[value] = v))}
              />
            </MapRow>
          ))}
        </MapSection>
      )}

      <MapSection title="People" note="Matched by email">
        {suggestions.people.map((person) => (
          <MapRow
            key={person.login}
            value={person.name}
            badge={
              person.kind === "existing" ? (
                <Badge tone="success">Existing person</Badge>
              ) : person.kind === "new" ? (
                <Badge tone="accent">New, disabled</Badge>
              ) : (
                <Badge title="Kept as an author under the same name. Can't sign in.">System account</Badge>
              )
            }
          >
            <span className="min-w-0 text-xs text-muted [overflow-wrap:anywhere]">{person.email ?? "No email in the source"}</span>
          </MapRow>
        ))}
        <p className="text-xs text-muted">
          New people are added as disabled accounts. Nobody is emailed. Activate them later on the Employees page.
          Accounts the source has no email for, such as "Deleted User", are kept as system accounts under the same name.
        </p>
      </MapSection>

      <MapSection title="Options">
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Estimation comes from"
            id="estimation-field"
            value={draft.estimationField ?? ""}
            onChange={(e) => change((d) => (d.estimationField = e.target.value || null))}
          >
            {suggestions.estimationFields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
            <option value="">Don't import estimates</option>
          </Select>
          <Select
            label="Sprints come from"
            id="sprint-board"
            value={draft.sprintBoard ?? ""}
            onChange={(e) => change((d) => (d.sprintBoard = e.target.value || null))}
          >
            {suggestions.boards.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name} ({b.sprints} sprint{b.sprints === 1 ? "" : "s"})
              </option>
            ))}
            <option value="">Don't import sprints</option>
          </Select>
        </div>
        {(suggestions.hierarchyLinks ?? []).length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">Hierarchy also comes from</legend>
            {suggestions.hierarchyLinks.map((link) => {
              const id = `hierarchy-${link.name.replace(/\W+/g, "-")}`
              return (
                <label key={link.name} className="flex items-start gap-2 text-sm" htmlFor={id}>
                  <input
                    id={id}
                    type="checkbox"
                    className="mt-1 accent-accent"
                    checked={(draft.hierarchyLinks ?? []).includes(link.name)}
                    onChange={(e) =>
                      change((d) => {
                        const rest = (d.hierarchyLinks ?? []).filter((n) => n !== link.name)
                        d.hierarchyLinks = e.target.checked ? [...rest, link.name] : rest
                      })
                    }
                  />
                  <span>
                    "{link.name}" links
                    <span className="ml-2 text-xs text-faint tabular-nums">{formatCount(link.links)} links</span>
                  </span>
                </label>
              )
            })}
            <p className="text-xs text-muted">
              {run.sourceProject.key} keeps epics, tasks and sub-tasks in these links as well as in YouTrack's own subtask links. Uncheck one
              to ignore it.
            </p>
          </fieldset>
        )}
        {(suggestions.restricted.issues > 0 || suggestions.restricted.comments > 0) && (
          <label className="flex items-start gap-2 text-sm" htmlFor="include-restricted">
            <input
              id="include-restricted"
              type="checkbox"
              className="mt-1 accent-accent"
              checked={draft.includeRestricted}
              onChange={(e) => change((d) => (d.includeRestricted = e.target.checked))}
            />
            <span>
              Include {formatCount(suggestions.restricted.issues)} issues and {formatCount(suggestions.restricted.comments)} comments
              with restricted visibility
              <span className="block text-xs text-muted">They become visible to everyone in this Moderniser project.</span>
            </span>
          </label>
        )}
      </MapSection>
    </StepLayout>
  )
}

function MapSection({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {note && <span className="text-xs text-faint">{note}</span>}
      </div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  )
}

function MapRow({
  value,
  uses,
  attention = false,
  badge,
  children,
}: {
  value: string
  uses?: string
  attention?: boolean
  badge: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded border px-2 py-1.5 text-sm sm:grid-cols-[minmax(0,1.2fr)_4.5rem_1rem_minmax(0,1.4fr)_8.5rem] ${
        attention ? "border-warning/40 bg-warning-soft" : "border-border"
      }`}
    >
      <span className="min-w-0 truncate text-foreground" title={value}>
        {value}
      </span>
      <span className="text-right text-[11px] text-faint tabular-nums">{uses}</span>
      <span className="hidden text-center text-faint sm:block" aria-hidden="true">
        →
      </span>
      <div className="col-span-2 min-w-0 sm:col-span-1">{children}</div>
      <span className="col-span-2 sm:col-span-1 sm:justify-self-end">{badge}</span>
    </div>
  )
}

function ChoiceSelect({
  label,
  value,
  choices,
  placeholder,
  allowCreate = false,
  onChange,
}: {
  label: string
  value: string
  choices: BoardChoice[]
  placeholder?: string
  allowCreate?: boolean
  onChange: (value: string) => void
}) {
  const current = choices.find((c) => c.id === value)
  return (
    <div className="flex min-w-0 items-center gap-2">
      {current?.category && <span className={`size-2 shrink-0 rounded-full ${CATEGORY_DOT[current.category]}`} aria-hidden="true" />}
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm outline-none focus:border-interactive focus:ring-2 focus:ring-interactive/30"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {choices.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
        {allowCreate && <option value={CREATE_ON_BOARD}>Create on the board</option>}
      </select>
    </div>
  )
}
